import { useCallback, useEffect, useState } from 'react';

import { Flow } from '../models';
import { SEED_FLOWS } from '../data/seedFlows';

const STORAGE_KEY = 'soyde:tech-checks:flows';

const clone = (flows: Flow[]): Flow[] =>
  JSON.parse(JSON.stringify(flows)) as Flow[];

const read = (): Flow[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return clone(SEED_FLOWS);
    const parsed = JSON.parse(raw) as Flow[];
    return Array.isArray(parsed) && parsed.length ? parsed : clone(SEED_FLOWS);
  } catch {
    return clone(SEED_FLOWS);
  }
};

export const useFlows = () => {
  const [flows, setFlows] = useState<Flow[]>(read);
  const [selectedId, setSelectedId] = useState<string>(() => read()[0]?.id ?? '');

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(flows));
    } catch {
      /* Sin localStorage la pantalla sigue andando: se pierde al recargar. */
    }
  }, [flows]);

  const selected = flows.find((flow) => flow.id === selectedId) ?? flows[0];

  const updateFlow = useCallback((updated: Flow) => {
    setFlows((current) =>
      current.map((flow) => (flow.id === updated.id ? updated : flow))
    );
  }, []);

  const createFlow = useCallback(() => {
    const flow: Flow = {
      id: crypto.randomUUID(),
      name: 'Secuencia nueva',
      description: '',
      variables: {},
      steps: [],
      links: [],
    };
    setFlows((current) => [...current, flow]);
    setSelectedId(flow.id);
  }, []);

  const duplicateFlow = useCallback((source: Flow) => {
    const copy: Flow = {
      ...JSON.parse(JSON.stringify(source)),
      id: crypto.randomUUID(),
      name: `${source.name} (copia)`,
    };
    setFlows((current) => [...current, copy]);
    setSelectedId(copy.id);
  }, []);

  const removeFlow = useCallback((id: string) => {
    setFlows((current) => {
      const rest = current.filter((flow) => flow.id !== id);
      const next = rest.length ? rest : clone(SEED_FLOWS);
      setSelectedId(next[0].id);
      return next;
    });
  }, []);

  const restoreSeeds = useCallback(() => {
    const seeds = clone(SEED_FLOWS);
    setFlows((current) => {
      const custom = current.filter(
        (flow) => !SEED_FLOWS.some((seed) => seed.id === flow.id)
      );
      return [...seeds, ...custom];
    });
    setSelectedId(seeds[0].id);
  }, []);

  const importFlows = useCallback((raw: string) => {
    const parsed = JSON.parse(raw) as Flow | Flow[];
    const incoming = Array.isArray(parsed) ? parsed : [parsed];
    if (!incoming.length) throw new Error('El archivo no trae ninguna secuencia');

    const withFreshIds = incoming.map((flow) => ({
      ...flow,
      id: crypto.randomUUID(),
    }));
    setFlows((current) => [...current, ...withFreshIds]);
    setSelectedId(withFreshIds[0].id);
    return withFreshIds.length;
  }, []);

  return {
    flows,
    selected,
    selectedId: selected?.id ?? '',
    setSelectedId,
    updateFlow,
    createFlow,
    duplicateFlow,
    removeFlow,
    restoreSeeds,
    importFlows,
  };
};
